// Global Budget Configuration Types
// This configuration applies to ALL budget types (Resource, Revenue, Cost)

// ── NEW (Phase 1 — Cost Centre Budgeting PRD) ──────────────────────────────
/**
 * Org-level budget mode. Mutually exclusive — one setting applies to all
 * projects across the organisation.
 *
 * - 'disabled'     → No budget tracking. All three budget tabs hidden on projects.
 * - 'category'     → Budget entered against cost categories. Current behaviour.
 * - 'cost-centre'  → Budget entered against cost centres within each project.
 */
export type BudgetMode = 'disabled' | 'category' | 'cost-centre';

/**
 * Full org-level budget configuration stored in localStorage (and eventually
 * synced to the server via PUT /api/organisations/{orgId}/budget-configuration).
 */
export interface BudgetConfiguration {
  /** The active budget mode for this organisation. */
  budgetMode: BudgetMode;

  // Category-Based mode only — ignored in 'disabled' and 'cost-centre' modes
  costBudgetEnabled: boolean;
  salesBudgetEnabled: boolean;
  resourceBudgetEnabled: boolean;

  /** True when projects with budget data exist — the budget mode cannot be changed. */
  hasBudgetData?: boolean;

  // Metadata
  lastUpdated?: string;
  updatedBy?: string;
}

/** Default org configuration — Cost Centre mode (demo default), all budget types on. */
export const DEFAULT_BUDGET_CONFIGURATION: BudgetConfiguration = {
  budgetMode: 'cost-centre',
  costBudgetEnabled: true,
  salesBudgetEnabled: true,
  resourceBudgetEnabled: true,
};

// ── DEPRECATED ─────────────────────────────────────────────────────────────
/**
 * @deprecated Use `BudgetMode` instead.
 * Kept as a type alias so existing callers (settings-project.tsx,
 * budget-type-selector.tsx, settings-old-full.tsx) continue to compile
 * during the Phase 1 → Phase 1 settings-UI transition (tasks 1.6–1.8).
 * Will be removed when those files are updated.
 */
export type BudgetTrackingType = 'simple' | 'monthly' | 'none';

// ── Support types (unchanged from previous version) ────────────────────────

export interface BudgetTypeInfo {
  type: BudgetTrackingType;
  label: string;
  icon: string;
  description: string;
  detailedDescription: string;
  affectedBudgets: string[];
}

// Role Budget Entry (used in project-level budget data)
export interface RoleBudgetEntry {
  id: string;
  role: string;
  budgetHours: number;
  rate: number;
  estimatedCost: number;
}

// Monthly Allocation (used in project-level budget data)
export interface MonthlyAllocation {
  month: string; // Format: 'YYYY-MM'
  hours: number;
}

// Monthly Role Budget (used in project-level budget data)
export interface MonthlyRoleBudget extends RoleBudgetEntry {
  monthlyAllocations: MonthlyAllocation[];
  allocatedHours: number; // Sum of monthly allocations
  isAllocated: boolean; // True if allocatedHours === budgetHours
}

// Budget Summary (used in project-level budget data)
export interface BudgetSummary {
  totalHours: number;
  totalEstimatedCost: number;
  roleCount: number;
}

// Validation errors
export interface ValidationError {
  field: string;
  message: string;
}

// Auto-fill options for monthly allocation
export type AutoFillStrategy =
  | 'even'
  | 'front-loaded'
  | 'back-loaded'
  | 'custom';

export interface AutoFillOptions {
  strategy: AutoFillStrategy;
  frontLoadPercent?: number; // For front-loaded (e.g., 60% in first half)
  backLoadPercent?: number; // For back-loaded (e.g., 60% in second half)
}
